import React, { useCallback, useMemo, useState } from 'react';
import { Search, Shield, AlertTriangle, MessageSquare, CheckCircle2, XCircle, Eye, Clock, Star, CreditCard, AlertOctagon, ChevronDown, ChevronRight, X, Lock, ExternalLink, Download, RefreshCw, Ban, ShieldAlert, Zap, UserX, Flag, ArrowRight, Check, Plus } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
interface AdminModerationCenterProps {
  complaints: any[];
  reviews: any[];
  setActiveTab: (tab: string) => void;
  setDetailPanel: (panel: any) => void;
  success: (msg: string) => void;
}
interface ModerationComplaint {
  id: string;
  date: string;
  author: string;
  authorType: 'user' | 'astrologer';
  target: string;
  targetType: 'user' | 'astrologer';
  type: 'payment' | 'fraud' | 'behavior' | 'ai' | 'consultation' | 'quality';
  linkedObject: string;
  linkedObjectType: 'order' | 'consultation' | 'chat' | 'report';
  description: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  risk: 'high' | 'medium' | 'low';
  status: 'open' | 'in_progress' | 'escalated' | 'resolved' | 'closed';
  assignee: string | null;
  slaMinutes: number;
  isRepeat: boolean;
  hasRefund: boolean;
  hasDispute: boolean;
  autoFlags: string[];
  previousComplaints: number;
  notes: {
    text: string;
    author: string;
    date: string;
  }[];
  userProfile: {
    email: string;
    phone: string;
    regDate: string;
    ltv: string;
    totalOrders: number;
    subscription: string | null;
  };
}
interface ModerationReview {
  id: string;
  date: string;
  user: string;
  astrologer: string;
  rating: number;
  text: string;
  status: 'pending' | 'approved' | 'rejected' | 'hidden';
}
interface ViolationEntry {
  id: string;
  date: string;
  violator: string;
  violatorType: 'user' | 'astrologer';
  action: string;
  reason: string;
  punishment: string;
  admin: string;
  isActive: boolean;
}
interface RiskFlag {
  id: string;
  type: string;
  target: string;
  description: string;
  severity: 'high' | 'medium' | 'low';
  date: string;
  status: 'active' | 'investigating' | 'resolved' | 'dismissed';
}
interface ActionLogEntry {
  id: string;
  date: string;
  admin: string;
  action: string;
  target: string;
  details: string;
}
// Build initial complaints from props
function buildComplaints(raw: any[]): ModerationComplaint[] {
  const types: ModerationComplaint['type'][] = ['payment', 'fraud', 'behavior', 'ai', 'consultation', 'quality'];
  const linkedTypes: ModerationComplaint['linkedObjectType'][] = ['order', 'consultation', 'chat', 'report'];
  return (raw || []).map((c, i) => {
    const slaMinutes = 30 + Math.floor(Math.random() * 210);
    return {
      id: `CMP-${1000 + i}`,
      date: c.date || `${15 - i} Окт 2024`,
      author: c.user || `Пользователь ${i + 1}`,
      authorType: 'user' as const,
      target: c.astrologer || `Астролог ${i + 1}`,
      targetType: 'astrologer' as const,
      type: types[i % types.length],
      linkedObject: `ORD-${2000 + i}`,
      linkedObjectType: linkedTypes[i % linkedTypes.length],
      description: c.reason || 'Описание жалобы отсутствует',
      priority: (i === 0 ? 'critical' : i < 3 ? 'high' : i < 5 ? 'medium' : 'low') as ModerationComplaint['priority'],
      risk: (i < 2 ? 'high' : i < 4 ? 'medium' : 'low') as ModerationComplaint['risk'],
      status: (c.status === 'in_progress' ? 'in_progress' : c.status === 'resolved' ? 'resolved' : c.status === 'closed' ? 'closed' : 'open') as ModerationComplaint['status'],
      assignee: i < 3 ? 'Алексей (Admin)' : null,
      slaMinutes,
      isRepeat: i === 0 || i === 3,
      hasRefund: i === 1 || i === 4,
      hasDispute: i === 2,
      autoFlags: i === 0 ? ['Повторная жалоба', 'Попытка увода клиента'] : i === 2 ? ['Резкий рост возвратов'] : [],
      previousComplaints: i === 0 ? 3 : i === 3 ? 2 : 0,
      notes: [],
      userProfile: {
        email: `user${i}@example.com`,
        phone: '+7 (999) 000-00-00',
        regDate: '12.05.2023',
        ltv: `${(i * 2000 + 5000).toLocaleString('ru-RU')} \u20BD`,
        totalOrders: i % 8 + 1,
        subscription: i % 2 === 0 ? 'Pro' : null
      }
    };
  });
}
function buildReviews(raw: any[]): ModerationReview[] {
  return (raw || []).map(r => ({
    id: String(r.id),
    date: r.date || '10 Окт 2024',
    user: r.user || r.reviewer || 'Пользователь',
    astrologer: r.astrologer || 'Астролог',
    rating: r.rating || 4,
    text: r.text || 'Отзыв',
    status: (r.status || 'pending') as ModerationReview['status']
  }));
}
const initialViolations: ViolationEntry[] = [{
  id: 'VL-001',
  date: '14 Окт 2024',
  violator: 'Максим Сатурн',
  violatorType: 'astrologer',
  action: 'Попытка увода клиента',
  reason: 'Передал личный номер телефона в чате',
  punishment: 'Блокировка на 7 дней',
  admin: 'Алексей (Admin)',
  isActive: true
}, {
  id: 'VL-002',
  date: '12 Окт 2024',
  violator: 'Алексей Новиков',
  violatorType: 'user',
  action: 'Мошенничество',
  reason: 'Чарджбэк после получения услуги',
  punishment: 'Полная блокировка',
  admin: 'Алексей (Admin)',
  isActive: true
}, {
  id: 'VL-003',
  date: '10 Окт 2024',
  violator: 'Иван Астров',
  violatorType: 'astrologer',
  action: 'Грубое общение',
  reason: 'Оскорбления в чате с клиентом',
  punishment: 'Предупреждение',
  admin: 'Мария (Модератор)',
  isActive: true
}, {
  id: 'VL-004',
  date: '08 Окт 2024',
  violator: 'Роман Фёдоров',
  violatorType: 'user',
  action: 'Спам жалобами',
  reason: '5 необоснованных жалоб за неделю',
  punishment: 'Ограничение подачи жалоб',
  admin: 'Алексей (Admin)',
  isActive: true
}, {
  id: 'VL-005',
  date: '05 Окт 2024',
  violator: 'Павел Орион',
  violatorType: 'astrologer',
  action: 'Некачественная услуга',
  reason: '3 жалобы за месяц, рейтинг ниже 3.0',
  punishment: 'Снятие с витрины на 14 дней',
  admin: 'Мария (Модератор)',
  isActive: true
}];
const initialRiskFlags: RiskFlag[] = [{
  id: 'RF-001',
  type: 'astrologer_complaints',
  target: 'Максим Сатурн',
  description: '4 жалобы за последний месяц',
  severity: 'high',
  date: '14 Окт 2024',
  status: 'active'
}, {
  id: 'RF-002',
  type: 'platform_escape',
  target: 'Иван Астров',
  description: 'Обнаружена передача контактов вне платформы',
  severity: 'high',
  date: '13 Окт 2024',
  status: 'active'
}, {
  id: 'RF-003',
  type: 'refund_spike',
  target: 'Павел Орион',
  description: 'Рост возвратов на 300% за неделю',
  severity: 'medium',
  date: '12 Окт 2024',
  status: 'active'
}, {
  id: 'RF-004',
  type: 'low_rating',
  target: 'Андрей Марс',
  description: 'Средняя оценка упала ниже 3.5',
  severity: 'medium',
  date: '11 Окт 2024',
  status: 'active'
}, {
  id: 'RF-005',
  type: 'suspicious',
  target: 'Роман Фёдоров',
  description: 'Подозрительная активность: множественные аккаунты',
  severity: 'high',
  date: '10 Окт 2024',
  status: 'active'
}];
const categoryTabs = [{
  id: 'complaints',
  label: 'Жалобы'
}, {
  id: 'reviews',
  label: 'Отзывы'
}, {
  id: 'violations',
  label: 'Нарушения'
}, {
  id: 'blocks',
  label: 'Блокировки'
}, {
  id: 'disputes',
  label: 'Споры и возвраты'
}, {
  id: 'risks',
  label: 'Риски'
}];
const ADMIN_NAME = 'Вы (Admin)';
const now = () => new Date().toLocaleString('ru-RU', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit'
});
export function AdminModerationCenter({
  complaints: rawComplaints,
  reviews: rawReviews,
  setActiveTab: setParentTab,
  setDetailPanel,
  success
}: AdminModerationCenterProps) {
  // ── Core mutable state ──
  const [complaintsList, setComplaintsList] = useState<ModerationComplaint[]>(() => buildComplaints(rawComplaints));
  const [reviewsList, setReviewsList] = useState<ModerationReview[]>(() => buildReviews(rawReviews));
  const [violations, setViolations] = useState<ViolationEntry[]>(initialViolations);
  const [riskFlags, setRiskFlags] = useState<RiskFlag[]>(initialRiskFlags);
  const [actionLog, setActionLog] = useState<ActionLogEntry[]>([]);
  // ── UI state ──
  const [activeTab, setActiveTab] = useState('complaints');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedTickets, setSelectedTickets] = useState<Set<string>>(new Set());
  const [metricFilter, setMetricFilter] = useState<string | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [noteText, setNoteText] = useState('');
  const [confirmAction, setConfirmAction] = useState<{
    label: string;
    action: () => void;
  } | null>(null);
  const [filters, setFilters] = useState({
    status: 'all',
    priority: 'all',
    risk: 'all',
    type: 'all'
  });
  // ── Derived ──
  const selectedItem = complaintsList.find(c => c.id === selectedId) || null;
  const getSlaStatus = (mins: number) => mins > 180 ? 'breached' : mins > 90 ? 'warning' : 'ok';
  const formatSla = (mins: number) => `${Math.floor(mins / 60)}ч ${mins % 60}м`;
  // ── Action logger ──
  const logAction = useCallback((action: string, target: string, details: string) => {
    setActionLog(prev => [{
      id: `AL-${Date.now()}`,
      date: now(),
      admin: ADMIN_NAME,
      action,
      target,
      details
    }, ...prev]);
  }, []);
  // ── Complaint actions ──
  const updateComplaint = useCallback((id: string, updates: Partial<ModerationComplaint>) => {
    setComplaintsList(prev => prev.map(c => c.id === id ? {
      ...c,
      ...updates
    } : c));
  }, []);
  const resolveComplaint = useCallback((id: string) => {
    updateComplaint(id, {
      status: 'resolved',
      assignee: ADMIN_NAME
    });
    logAction('Решена', id, 'Жалоба помечена как решённая');
    success(`Жалоба ${id} решена`);
  }, [updateComplaint, logAction, success]);
  const closeComplaint = useCallback((id: string) => {
    updateComplaint(id, {
      status: 'closed',
      assignee: ADMIN_NAME
    });
    logAction('Закрыта', id, 'Жалоба закрыта');
    success(`Жалоба ${id} закрыта`);
  }, [updateComplaint, logAction, success]);
  const takeInProgress = useCallback((id: string) => {
    updateComplaint(id, {
      status: 'in_progress',
      assignee: ADMIN_NAME
    });
    logAction('Взята в работу', id, 'Жалоба взята в работу');
    success(`Жалоба ${id} взята в работу`);
  }, [updateComplaint, logAction, success]);
  const escalateComplaint = useCallback((id: string) => {
    updateComplaint(id, {
      status: 'escalated',
      priority: 'critical',
      assignee: 'Руководство'
    });
    logAction('Эскалация', id, 'Жалоба эскалирована руководству');
    success(`Жалоба ${id} эскалирована`);
  }, [updateComplaint, logAction, success]);
  const requestClarification = useCallback((id: string) => {
    updateComplaint(id, {
      status: 'in_progress',
      assignee: ADMIN_NAME
    });
    logAction('Запрос уточнения', id, 'Запрошено уточнение у автора');
    success(`Запрос уточнения отправлен по ${id}`);
  }, [updateComplaint, logAction, success]);
  const issueRefund = useCallback((id: string) => {
    updateComplaint(id, {
      hasRefund: true,
      status: 'resolved',
      assignee: ADMIN_NAME
    });
    logAction('Возврат', id, 'Оформлен возврат средств');
    success(`Возврат оформлен по ${id}`);
  }, [updateComplaint, logAction, success]);
  const warnTarget = useCallback((complaint: ModerationComplaint) => {
    const vlId = `VL-${Date.now()}`;
    setViolations(prev => [{
      id: vlId,
      date: now(),
      violator: complaint.target,
      violatorType: complaint.targetType,
      action: 'Предупреждение по жалобе',
      reason: complaint.description,
      punishment: 'Предупреждение',
      admin: ADMIN_NAME,
      isActive: true
    }, ...prev]);
    updateComplaint(complaint.id, {
      status: 'resolved',
      assignee: ADMIN_NAME
    });
    logAction('Предупреждение', complaint.target, `Предупреждение выдано по жалобе ${complaint.id}`);
    success(`Предупреждение отправлено: ${complaint.target}`);
  }, [updateComplaint, logAction, success]);
  const restrictTarget = useCallback((complaint: ModerationComplaint) => {
    const vlId = `VL-${Date.now()}`;
    setViolations(prev => [{
      id: vlId,
      date: now(),
      violator: complaint.target,
      violatorType: complaint.targetType,
      action: 'Временное ограничение',
      reason: complaint.description,
      punishment: 'Ограничение на 7 дней',
      admin: ADMIN_NAME,
      isActive: true
    }, ...prev]);
    updateComplaint(complaint.id, {
      status: 'resolved',
      assignee: ADMIN_NAME
    });
    logAction('Ограничение', complaint.target, `Временное ограничение по жалобе ${complaint.id}`);
    success(`Ограничение установлено: ${complaint.target}`);
  }, [updateComplaint, logAction, success]);
  const blockTarget = useCallback((complaint: ModerationComplaint) => {
    const vlId = `VL-${Date.now()}`;
    setViolations(prev => [{
      id: vlId,
      date: now(),
      violator: complaint.target,
      violatorType: complaint.targetType,
      action: 'Блокировка по жалобе',
      reason: complaint.description,
      punishment: 'Полная блокировка',
      admin: ADMIN_NAME,
      isActive: true
    }, ...prev]);
    updateComplaint(complaint.id, {
      status: 'resolved',
      assignee: ADMIN_NAME
    });
    logAction('Блокировка', complaint.target, `Заблокирован по жалобе ${complaint.id}`);
    success(`${complaint.target} заблокирован`);
    setConfirmAction(null);
  }, [updateComplaint, logAction, success]);
  const addNote = useCallback((id: string, text: string) => {
    setComplaintsList(prev => prev.map(c => c.id === id ? {
      ...c,
      notes: [...c.notes, {
        text,
        author: ADMIN_NAME,
        date: now()
      }]
    } : c));
    logAction('Заметка', id, text);
    success('Заметка добавлена');
  }, [logAction, success]);
  // ── Review actions ──
  const approveReview = useCallback((id: string) => {
    setReviewsList(prev => prev.map(r => r.id === id ? {
      ...r,
      status: 'approved' as const
    } : r));
    logAction('Одобрен отзыв', `REV-${id}`, 'Отзыв опубликован');
    success(`Отзыв REV-${id} опубликован`);
  }, [logAction, success]);
  const rejectReview = useCallback((id: string) => {
    setReviewsList(prev => prev.map(r => r.id === id ? {
      ...r,
      status: 'rejected' as const
    } : r));
    logAction('Отклонён отзыв', `REV-${id}`, 'Отзыв отклонён');
    success(`Отзыв REV-${id} отклонён`);
  }, [logAction, success]);
  const hideReview = useCallback((id: string) => {
    setReviewsList(prev => prev.map(r => r.id === id ? {
      ...r,
      status: 'hidden' as const
    } : r));
    logAction('Скрыт отзыв', `REV-${id}`, 'Отзыв скрыт');
    success(`Отзыв REV-${id} скрыт`);
  }, [logAction, success]);
  // ── Violation / Block actions ──
  const unblockViolation = useCallback((id: string) => {
    setViolations(prev => prev.map(v => v.id === id ? {
      ...v,
      isActive: false,
      punishment: v.punishment + ' (снято)'
    } : v));
    const viol = violations.find(v => v.id === id);
    logAction('Разблокировка', viol?.violator || id, `Снята блокировка ${id}`);
    success(`${viol?.violator || 'Пользователь'} разблокирован`);
  }, [violations, logAction, success]);
  // ── Risk actions ──
  const investigateRisk = useCallback((id: string) => {
    setRiskFlags(prev => prev.map(r => r.id === id ? {
      ...r,
      status: 'investigating' as const
    } : r));
    logAction('Проверка риска', id, 'Начата проверка');
    success(`Проверка ${id} начата`);
  }, [logAction, success]);
  const resolveRisk = useCallback((id: string) => {
    setRiskFlags(prev => prev.map(r => r.id === id ? {
      ...r,
      status: 'resolved' as const
    } : r));
    logAction('Риск закрыт', id, 'Риск помечен как решённый');
    success(`Риск ${id} закрыт`);
  }, [logAction, success]);
  const blockFromRisk = useCallback((rf: RiskFlag) => {
    const vlId = `VL-${Date.now()}`;
    setViolations(prev => [{
      id: vlId,
      date: now(),
      violator: rf.target,
      violatorType: 'astrologer',
      action: 'Блокировка по риску',
      reason: rf.description,
      punishment: 'Полная блокировка',
      admin: ADMIN_NAME,
      isActive: true
    }, ...prev]);
    setRiskFlags(prev => prev.map(r => r.id === rf.id ? {
      ...r,
      status: 'resolved' as const
    } : r));
    logAction('Блокировка по риску', rf.target, rf.description);
    success(`${rf.target} заблокирован`);
    setConfirmAction(null);
  }, [logAction, success]);
  // ── Mass actions ──
  const massResolve = useCallback(() => {
    const ids = Array.from(selectedTickets);
    setComplaintsList(prev => prev.map(c => ids.includes(c.id) ? {
      ...c,
      status: 'resolved' as const,
      assignee: ADMIN_NAME
    } : c));
    logAction('Массовое решение', ids.join(', '), `${ids.length} жалоб решено`);
    success(`${ids.length} жалоб решено`);
    setSelectedTickets(new Set());
  }, [selectedTickets, logAction, success]);
  const massClose = useCallback(() => {
    const ids = Array.from(selectedTickets);
    setComplaintsList(prev => prev.map(c => ids.includes(c.id) ? {
      ...c,
      status: 'closed' as const,
      assignee: ADMIN_NAME
    } : c));
    logAction('Массовое закрытие', ids.join(', '), `${ids.length} жалоб закрыто`);
    success(`${ids.length} жалоб закрыто`);
    setSelectedTickets(new Set());
  }, [selectedTickets, logAction, success]);
  const massAssign = useCallback(() => {
    const ids = Array.from(selectedTickets);
    setComplaintsList(prev => prev.map(c => ids.includes(c.id) ? {
      ...c,
      status: 'in_progress' as const,
      assignee: ADMIN_NAME
    } : c));
    logAction('Массовое назначение', ids.join(', '), `${ids.length} жалоб назначено`);
    success(`${ids.length} жалоб назначено на вас`);
    setSelectedTickets(new Set());
  }, [selectedTickets, logAction, success]);
  // ── Metrics ──
  const metrics = useMemo(() => ({
    newToday: complaintsList.filter(c => c.status === 'open').length,
    inProgress: complaintsList.filter(c => c.status === 'in_progress').length,
    overdue: complaintsList.filter(c => getSlaStatus(c.slaMinutes) === 'breached').length,
    highRisk: complaintsList.filter(c => c.risk === 'high').length,
    withRefunds: complaintsList.filter(c => c.hasRefund).length,
    onAstrologers: complaintsList.filter(c => c.targetType === 'astrologer').length,
    repeats: complaintsList.filter(c => c.isRepeat).length,
    blocked: violations.filter(v => v.isActive && v.punishment.toLowerCase().includes('блокировка')).length,
    pendingReviews: reviewsList.filter(r => r.status === 'pending').length
  }), [complaintsList, violations, reviewsList]);
  // ── Filtered data ──
  const filteredComplaints = useMemo(() => {
    let result = [...complaintsList];
    if (metricFilter === 'newToday') result = result.filter(c => c.status === 'open');else if (metricFilter === 'inProgress') result = result.filter(c => c.status === 'in_progress');else if (metricFilter === 'overdue') result = result.filter(c => getSlaStatus(c.slaMinutes) === 'breached');else if (metricFilter === 'highRisk') result = result.filter(c => c.risk === 'high');else if (metricFilter === 'withRefunds') result = result.filter(c => c.hasRefund);else if (metricFilter === 'onAstrologers') result = result.filter(c => c.targetType === 'astrologer');else if (metricFilter === 'repeats') result = result.filter(c => c.isRepeat);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(c => c.id.toLowerCase().includes(q) || c.author.toLowerCase().includes(q) || c.target.toLowerCase().includes(q) || c.description.toLowerCase().includes(q));
    }
    if (filters.status !== 'all') result = result.filter(c => c.status === filters.status);
    if (filters.priority !== 'all') result = result.filter(c => c.priority === filters.priority);
    if (filters.risk !== 'all') result = result.filter(c => c.risk === filters.risk);
    if (filters.type !== 'all') result = result.filter(c => c.type === filters.type);
    return result;
  }, [complaintsList, searchQuery, filters, metricFilter]);
  const filteredReviews = useMemo(() => {
    let result = [...reviewsList];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(r => r.id.includes(q) || r.user.toLowerCase().includes(q) || r.astrologer.toLowerCase().includes(q));
    }
    if (filters.status !== 'all') result = result.filter(r => r.status === filters.status);
    return result;
  }, [reviewsList, searchQuery, filters]);
  // ── UI helpers ──
  const toggleSection = (s: string) => {
    setCollapsedSections(prev => {
      const n = new Set(prev);
      if (n.has(s)) n.delete(s);else n.add(s);
      return n;
    });
  };
  const toggleSelect = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedTickets(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);else n.add(id);
      return n;
    });
  };
  const getTypeLabel = (t: string) => ({
    payment: 'Оплата',
    fraud: 'Обман',
    behavior: 'Поведение',
    ai: 'ИИ',
    consultation: 'Консультация',
    quality: 'Качество'
  })[t] || t;
  const getTypeIcon = (t: string) => {
    switch (t) {
      case 'payment':
        return <CreditCard className="w-3.5 h-3.5 text-amber-500" data-id="element-6018" />;
      case 'fraud':
        return <ShieldAlert className="w-3.5 h-3.5 text-rose-500" data-id="element-6019" />;
      case 'behavior':
        return <UserX className="w-3.5 h-3.5 text-orange-500" data-id="element-6020" />;
      case 'ai':
        return <Zap className="w-3.5 h-3.5 text-purple-500" data-id="element-6021" />;
      case 'consultation':
        return <MessageSquare className="w-3.5 h-3.5 text-blue-500" data-id="element-6022" />;
      default:
        return <Flag className="w-3.5 h-3.5 text-slate-400" data-id="element-6023" />;
    }
  };
  const getPriorityBadge = (p: string) => {
    if (p === 'critical') return <Badge variant="error" size="sm" data-id="element-6024">
          Критический
        </Badge>;
    if (p === 'high') return <Badge variant="error" size="sm" data-id="element-6025">
          Высокий
        </Badge>;
    if (p === 'medium') return <Badge variant="warning" size="sm" data-id="element-6026">
          Средний
        </Badge>;
    return <Badge variant="neutral" size="sm" data-id="element-6027">
        Низкий
      </Badge>;
  };
  const getStatusBadge = (s: string) => {
    switch (s) {
      case 'open':
      case 'new':
        return <Badge variant="error" size="sm" data-id="element-6028">
            Новая
          </Badge>;
      case 'in_progress':
        return <Badge variant="warning" size="sm" data-id="element-6029">
            В работе
          </Badge>;
      case 'escalated':
        return <Badge variant="error" size="sm" data-id="element-6030">
            Эскалация
          </Badge>;
      case 'resolved':
        return <Badge variant="success" size="sm" data-id="element-6031">
            Решена
          </Badge>;
      case 'closed':
        return <Badge variant="neutral" size="sm" data-id="element-6032">
            Закрыта
          </Badge>;
      case 'pending':
        return <Badge variant="warning" size="sm" data-id="element-6033">
            На проверке
          </Badge>;
      case 'approved':
        return <Badge variant="success" size="sm" data-id="element-6034">
            Опубликован
          </Badge>;
      case 'rejected':
        return <Badge variant="error" size="sm" data-id="element-6035">
            Отклонён
          </Badge>;
      case 'hidden':
        return <Badge variant="neutral" size="sm" data-id="element-6036">
            Скрыт
          </Badge>;
      case 'investigating':
        return <Badge variant="warning" size="sm" data-id="element-6037">
            Проверяется
          </Badge>;
      case 'dismissed':
        return <Badge variant="neutral" size="sm" data-id="element-6038">
            Отклонён
          </Badge>;
      case 'active':
        return <Badge variant="error" size="sm" data-id="element-6039">
            Активен
          </Badge>;
      default:
        return <Badge variant="neutral" size="sm" data-id="element-6040">
            {s}
          </Badge>;
    }
  };
  const getRiskBadge = (r: string) => {
    if (r === 'high') return <span className="text-[10px] font-bold text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded" data-id="element-6041">
          Высокий
        </span>;
    if (r === 'medium') return <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded" data-id="element-6042">
          Средний
        </span>;
    return <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded" data-id="element-6043">
        Низкий
      </span>;
  };
  const getSlaColor = (mins: number) => {
    const s = getSlaStatus(mins);
    if (s === 'breached') return 'text-rose-600 bg-rose-50 border-rose-200';
    if (s === 'warning') return 'text-amber-600 bg-amber-50 border-amber-200';
    return 'text-emerald-600 bg-emerald-50 border-emerald-200';
  };
  const isTerminal = (s: string) => s === 'resolved' || s === 'closed';
  const metricCards = [{
    key: 'newToday',
    label: 'Новые',
    value: metrics.newToday,
    icon: <AlertTriangle className="w-4 h-4" data-id="element-6044" />,
    color: 'text-rose-600 bg-rose-50',
    tab: 'complaints'
  }, {
    key: 'inProgress',
    label: 'В работе',
    value: metrics.inProgress,
    icon: <Clock className="w-4 h-4" data-id="element-6045" />,
    color: 'text-blue-600 bg-blue-50',
    tab: 'complaints'
  }, {
    key: 'overdue',
    label: 'Просрочено',
    value: metrics.overdue,
    icon: <AlertOctagon className="w-4 h-4" data-id="element-6046" />,
    color: 'text-rose-600 bg-rose-50',
    tab: 'complaints'
  }, {
    key: 'highRisk',
    label: 'Высокий риск',
    value: metrics.highRisk,
    icon: <ShieldAlert className="w-4 h-4" data-id="element-6047" />,
    color: 'text-orange-600 bg-orange-50',
    tab: 'complaints'
  }, {
    key: 'withRefunds',
    label: 'С возвратами',
    value: metrics.withRefunds,
    icon: <CreditCard className="w-4 h-4" data-id="element-6048" />,
    color: 'text-purple-600 bg-purple-50',
    tab: 'disputes'
  }, {
    key: 'onAstrologers',
    label: 'На астрологов',
    value: metrics.onAstrologers,
    icon: <Star className="w-4 h-4" data-id="element-6049" />,
    color: 'text-amber-600 bg-amber-50',
    tab: 'complaints'
  }, {
    key: 'repeats',
    label: 'Повторные',
    value: metrics.repeats,
    icon: <RefreshCw className="w-4 h-4" data-id="element-6050" />,
    color: 'text-indigo-600 bg-indigo-50',
    tab: 'complaints'
  }, {
    key: 'blocked',
    label: 'Блокировки',
    value: metrics.blocked,
    icon: <Ban className="w-4 h-4" data-id="element-6051" />,
    color: 'text-slate-600 bg-slate-100',
    tab: 'blocks'
  }, {
    key: 'pendingReviews',
    label: 'Отзывы',
    value: metrics.pendingReviews,
    icon: <MessageSquare className="w-4 h-4" data-id="element-6052" />,
    color: 'text-emerald-600 bg-emerald-50',
    tab: 'reviews'
  }];
  const handleMetricClick = (m: (typeof metricCards)[0]) => {
    if (m.key === 'blocked') {
      setActiveTab('blocks');
      setMetricFilter(null);
      return;
    }
    if (m.key === 'pendingReviews') {
      setActiveTab('reviews');
      setMetricFilter(null);
      return;
    }
    if (m.key === 'withRefunds') {
      setActiveTab('disputes');
      setMetricFilter(null);
      return;
    }
    setActiveTab('complaints');
    setMetricFilter(metricFilter === m.key ? null : m.key);
  };
  return <div className="space-y-3 h-[calc(100vh-10rem)] min-h-[700px] flex flex-col" data-id="element-6053">
      {/* Confirm dialog */}
      {confirmAction && <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={() => setConfirmAction(null)} data-id="element-6054">
          <div className="bg-white rounded-xl p-6 shadow-2xl max-w-sm w-full mx-4" onClick={e => e.stopPropagation()} data-id="element-6055">
            <h3 className="text-base font-bold text-slate-900 mb-2" data-id="element-6056">
              Подтверждение
            </h3>
            <p className="text-sm text-slate-600 mb-4" data-id="element-6057">
              Вы уверены, что хотите выполнить действие:{' '}
              <strong data-id="element-6058">{confirmAction.label}</strong>?
            </p>
            <div className="flex gap-2 justify-end" data-id="element-6059">
              <Button variant="outline" size="sm" onClick={() => setConfirmAction(null)} data-id="element-6060">
                Отмена
              </Button>
              <Button variant="primary" size="sm" className="bg-rose-600 hover:bg-rose-700" onClick={confirmAction.action} data-id="element-6061">
                Подтвердить
              </Button>
            </div>
          </div>
        </div>}

      {/* Header */}
      <div className="flex items-center justify-between flex-shrink-0" data-id="element-6062">
        <div data-id="element-6063">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2" data-id="element-6064">
            <Shield className="w-5 h-5 text-indigo-600" data-id="element-6065" /> Центр модерации
          </h2>
          <p className="text-xs text-slate-500" data-id="element-6066">
            {filteredComplaints.length} жалоб, {filteredReviews.length} отзывов,{' '}
            {actionLog.length} действий
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-6067">
          <Button variant="outline" size="sm" icon={<Download className="w-4 h-4" data-id="element-6069" />} onClick={() => {
          logAction('Экспорт', 'all', 'Экспорт данных');
          success('Экспорт начат');
        }} data-id="element-6068">
            Экспорт
          </Button>
          <Button variant="outline" size="sm" icon={<RefreshCw className="w-4 h-4" data-id="element-6071" />} onClick={() => {
          setComplaintsList(buildComplaints(rawComplaints));
          setReviewsList(buildReviews(rawReviews));
          success('Данные обновлены');
        }} data-id="element-6070">
            Обновить
          </Button>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-3 lg:grid-cols-9 gap-2 flex-shrink-0" data-id="element-6072">
        {metricCards.map(m => <button key={m.key} onClick={() => handleMetricClick(m)} className={`flex items-center gap-2 p-2 rounded-xl border transition-all text-left ${metricFilter === m.key && activeTab === 'complaints' ? 'border-indigo-300 bg-indigo-50 ring-1 ring-indigo-200' : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'}`} data-id="element-6073">
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${m.color}`} data-id="element-6074">
              {m.icon}
            </div>
            <div className="min-w-0" data-id="element-6075">
              <div className="text-base font-bold text-slate-900 leading-none" data-id="element-6076">
                {m.value}
              </div>
              <div className="text-[9px] text-slate-500 truncate" data-id="element-6077">
                {m.label}
              </div>
            </div>
          </button>)}
      </div>

      {/* Tabs + Controls */}
      <div className="flex items-center gap-2 flex-shrink-0 flex-wrap" data-id="element-6078">
        <div className="flex gap-1 overflow-x-auto" data-id="element-6079">
          {categoryTabs.map(tab => <button key={tab.id} onClick={() => {
          setActiveTab(tab.id);
          setSelectedId(null);
          setMetricFilter(null);
        }} className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors ${activeTab === tab.id ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-6080">
              {tab.label}
              {tab.id === 'reviews' && metrics.pendingReviews > 0 && <span className="ml-1 bg-white/20 text-[9px] px-1 rounded" data-id="element-6081">
                  {metrics.pendingReviews}
                </span>}
            </button>)}
        </div>
        <div className="flex-1" data-id="element-6082" />
        <button onClick={() => setShowFilters(!showFilters)} className={`p-1.5 rounded-lg transition-colors ${showFilters ? 'bg-indigo-100 text-indigo-600' : 'text-slate-400 hover:bg-slate-100'}`} data-id="element-6083">
          <Search className="w-4 h-4" data-id="element-6084" />
        </button>
        {selectedTickets.size > 0 && <div className="flex items-center gap-1 ml-2 pl-2 border-l border-slate-200" data-id="element-6085">
            <span className="text-xs text-slate-500" data-id="element-6086">
              {selectedTickets.size} выбрано
            </span>
            <Button variant="outline" size="sm" className="text-[10px] h-7 px-2" onClick={massAssign} data-id="element-6087">
              Назначить
            </Button>
            <Button variant="outline" size="sm" className="text-[10px] h-7 px-2" onClick={massResolve} data-id="element-6088">
              Решить
            </Button>
            <Button variant="outline" size="sm" className="text-[10px] h-7 px-2" onClick={massClose} data-id="element-6089">
              Закрыть
            </Button>
            <Button variant="ghost" size="sm" className="text-[10px] h-7 px-2" onClick={() => setSelectedTickets(new Set())} data-id="element-6090">
              Снять
            </Button>
          </div>}
      </div>

      {/* Filters */}
      {showFilters && <div className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl flex-shrink-0 flex-wrap" data-id="element-6091">
          <select className="rounded-lg border border-slate-200 bg-white text-xs py-1.5 px-2" value={filters.status} onChange={e => setFilters({
        ...filters,
        status: e.target.value
      })} data-id="element-6092">
            <option value="all" data-id="element-6093">Статус: все</option>
            <option value="open" data-id="element-6094">Новая</option>
            <option value="in_progress" data-id="element-6095">В работе</option>
            <option value="escalated" data-id="element-6096">Эскалация</option>
            <option value="resolved" data-id="element-6097">Решена</option>
            <option value="closed" data-id="element-6098">Закрыта</option>
          </select>
          <select className="rounded-lg border border-slate-200 bg-white text-xs py-1.5 px-2" value={filters.priority} onChange={e => setFilters({
        ...filters,
        priority: e.target.value
      })} data-id="element-6099">
            <option value="all" data-id="element-6100">Приоритет: все</option>
            <option value="critical" data-id="element-6101">Критический</option>
            <option value="high" data-id="element-6102">Высокий</option>
            <option value="medium" data-id="element-6103">Средний</option>
            <option value="low" data-id="element-6104">Низкий</option>
          </select>
          <select className="rounded-lg border border-slate-200 bg-white text-xs py-1.5 px-2" value={filters.risk} onChange={e => setFilters({
        ...filters,
        risk: e.target.value
      })} data-id="element-6105">
            <option value="all" data-id="element-6106">Риск: все</option>
            <option value="high" data-id="element-6107">Высокий</option>
            <option value="medium" data-id="element-6108">Средний</option>
            <option value="low" data-id="element-6109">Низкий</option>
          </select>
          <select className="rounded-lg border border-slate-200 bg-white text-xs py-1.5 px-2" value={filters.type} onChange={e => setFilters({
        ...filters,
        type: e.target.value
      })} data-id="element-6110">
            <option value="all" data-id="element-6111">Тип: все</option>
            <option value="payment" data-id="element-6112">Оплата</option>
            <option value="fraud" data-id="element-6113">Обман</option>
            <option value="behavior" data-id="element-6114">Поведение</option>
            <option value="ai" data-id="element-6115">ИИ</option>
            <option value="consultation" data-id="element-6116">Консультация</option>
            <option value="quality" data-id="element-6117">Качество</option>
          </select>
          <button onClick={() => {
        setFilters({
          status: 'all',
          priority: 'all',
          risk: 'all',
          type: 'all'
        });
        setMetricFilter(null);
      }} className="text-xs text-indigo-600 hover:text-indigo-700 font-medium" data-id="element-6118">
            Сбросить
          </button>
        </div>}

      {/* Search */}
      <div className="flex-shrink-0" data-id="element-6119">
        <div className="relative" data-id="element-6120">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" data-id="element-6121" />
          <input type="text" placeholder="Поиск по ID, автору, объекту жалобы..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 bg-white" data-id="element-6122" />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex gap-0 overflow-hidden rounded-xl border border-slate-200 bg-white" data-id="element-6123">
        {/* Table */}
        <div className={`flex-1 overflow-auto min-w-0 ${selectedItem ? 'hidden lg:block' : ''}`} data-id="element-6124">
          {/* COMPLAINTS */}
          {activeTab === 'complaints' && <table className="w-full text-left" data-id="element-6125">
              <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[10px] uppercase tracking-wider sticky top-0 z-10" data-id="element-6126">
                <tr data-id="element-6127">
                  <th className="px-3 py-2.5 font-medium w-8" data-id="element-6128"></th>
                  <th className="px-3 py-2.5 font-medium" data-id="element-6129">ID</th>
                  <th className="px-3 py-2.5 font-medium" data-id="element-6130">Автор</th>
                  <th className="px-3 py-2.5 font-medium" data-id="element-6131">На кого</th>
                  <th className="px-3 py-2.5 font-medium" data-id="element-6132">Тип</th>
                  <th className="px-3 py-2.5 font-medium" data-id="element-6133">Описание</th>
                  <th className="px-3 py-2.5 font-medium" data-id="element-6134">Приоритет</th>
                  <th className="px-3 py-2.5 font-medium" data-id="element-6135">Риск</th>
                  <th className="px-3 py-2.5 font-medium" data-id="element-6136">Статус</th>
                  <th className="px-3 py-2.5 font-medium" data-id="element-6137">SLA</th>
                  <th className="px-3 py-2.5 font-medium text-right" data-id="element-6138">
                    Действия
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50" data-id="element-6139">
                {filteredComplaints.map(c => <tr key={c.id} onClick={() => setSelectedId(c.id)} className={`cursor-pointer transition-colors ${selectedId === c.id ? 'bg-indigo-50/50' : isTerminal(c.status) ? 'bg-slate-50/50 opacity-60' : 'hover:bg-slate-50'}`} data-id="element-6140">
                    <td className="px-3 py-2.5" data-id="element-6141">
                      <button onClick={e => toggleSelect(c.id, e)} className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${selectedTickets.has(c.id) ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-slate-300 hover:border-indigo-400'}`} data-id="element-6142">
                        {selectedTickets.has(c.id) && <Check className="w-3 h-3" data-id="element-6143" />}
                      </button>
                    </td>
                    <td className="px-3 py-2.5" data-id="element-6144">
                      <div className="text-xs font-bold text-slate-900" data-id="element-6145">
                        {c.id}
                      </div>
                      <div className="text-[10px] text-slate-400" data-id="element-6146">{c.date}</div>
                    </td>
                    <td className="px-3 py-2.5" data-id="element-6147">
                      <span className="text-xs font-medium text-indigo-600" data-id="element-6148">
                        {c.author}
                      </span>
                    </td>
                    <td className="px-3 py-2.5" data-id="element-6149">
                      <div className="flex items-center gap-1.5" data-id="element-6150">
                        <span className="text-xs font-medium text-slate-700" data-id="element-6151">
                          {c.target}
                        </span>
                        {c.isRepeat && <span className="text-[9px] font-bold text-rose-600 bg-rose-50 px-1 rounded" data-id="element-6152">
                            Повтор
                          </span>}
                      </div>
                    </td>
                    <td className="px-3 py-2.5" data-id="element-6153">
                      <div className="flex items-center gap-1" data-id="element-6154">
                        {getTypeIcon(c.type)}
                        <span className="text-xs text-slate-600" data-id="element-6155">
                          {getTypeLabel(c.type)}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5" data-id="element-6156">
                      <p className="text-xs text-slate-600 truncate max-w-[180px]" data-id="element-6157">
                        {c.description}
                      </p>
                    </td>
                    <td className="px-3 py-2.5" data-id="element-6158">
                      {getPriorityBadge(c.priority)}
                    </td>
                    <td className="px-3 py-2.5" data-id="element-6159">{getRiskBadge(c.risk)}</td>
                    <td className="px-3 py-2.5" data-id="element-6160">{getStatusBadge(c.status)}</td>
                    <td className="px-3 py-2.5" data-id="element-6161">
                      <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${getSlaColor(c.slaMinutes)}`} data-id="element-6162">
                        {formatSla(c.slaMinutes)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right" data-id="element-6163">
                      <div className="flex justify-end gap-1" data-id="element-6164">
                        {c.status === 'open' && <button onClick={e => {
                    e.stopPropagation();
                    takeInProgress(c.id);
                  }} className="p-1 text-blue-600 hover:bg-blue-50 rounded" title="Взять в работу" data-id="element-6165">
                            <Clock className="w-3.5 h-3.5" data-id="element-6166" />
                          </button>}
                        {!isTerminal(c.status) && <button onClick={e => {
                    e.stopPropagation();
                    resolveComplaint(c.id);
                  }} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded" title="Решить" data-id="element-6167">
                            <CheckCircle2 className="w-3.5 h-3.5" data-id="element-6168" />
                          </button>}
                        <button onClick={e => {
                    e.stopPropagation();
                    setSelectedId(c.id);
                  }} className="p-1 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded" title="Подробнее" data-id="element-6169">
                          <Eye className="w-3.5 h-3.5" data-id="element-6170" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
                {filteredComplaints.length === 0 && <tr data-id="element-6171">
                    <td colSpan={11} className="px-4 py-12 text-center text-sm text-slate-400" data-id="element-6172">
                      Жалобы не найдены
                    </td>
                  </tr>}
              </tbody>
            </table>}

          {/* REVIEWS */}
          {activeTab === 'reviews' && <table className="w-full text-left" data-id="element-6173">
              <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[10px] uppercase tracking-wider sticky top-0 z-10" data-id="element-6174">
                <tr data-id="element-6175">
                  <th className="px-3 py-2.5 font-medium" data-id="element-6176">ID</th>
                  <th className="px-3 py-2.5 font-medium" data-id="element-6177">Автор</th>
                  <th className="px-3 py-2.5 font-medium" data-id="element-6178">Астролог</th>
                  <th className="px-3 py-2.5 font-medium" data-id="element-6179">Оценка</th>
                  <th className="px-3 py-2.5 font-medium" data-id="element-6180">Текст</th>
                  <th className="px-3 py-2.5 font-medium" data-id="element-6181">Статус</th>
                  <th className="px-3 py-2.5 font-medium text-right" data-id="element-6182">
                    Действия
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50" data-id="element-6183">
                {filteredReviews.map(r => <tr key={r.id} className={`transition-colors ${r.status !== 'pending' ? 'opacity-60' : 'hover:bg-slate-50'}`} data-id="element-6184">
                    <td className="px-3 py-2.5" data-id="element-6185">
                      <div className="text-xs font-bold text-slate-900" data-id="element-6186">
                        REV-{r.id}
                      </div>
                      <div className="text-[10px] text-slate-400" data-id="element-6187">{r.date}</div>
                    </td>
                    <td className="px-3 py-2.5 text-xs font-medium text-slate-700" data-id="element-6188">
                      {r.user}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-600" data-id="element-6189">
                      {r.astrologer}
                    </td>
                    <td className="px-3 py-2.5" data-id="element-6190">
                      <div className="flex items-center gap-1" data-id="element-6191">
                        <span className="font-bold text-sm text-slate-900" data-id="element-6192">
                          {r.rating}
                        </span>
                        <span className="text-amber-400" data-id="element-6193">★</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5" data-id="element-6194">
                      <p className="text-xs text-slate-600 truncate max-w-[250px]" data-id="element-6195">
                        {r.text}
                      </p>
                    </td>
                    <td className="px-3 py-2.5" data-id="element-6196">{getStatusBadge(r.status)}</td>
                    <td className="px-3 py-2.5 text-right" data-id="element-6197">
                      <div className="flex justify-end gap-1" data-id="element-6198">
                        {r.status === 'pending' && <>
                            <button onClick={() => approveReview(r.id)} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded" title="Опубликовать" data-id="element-6199">
                              <CheckCircle2 className="w-3.5 h-3.5" data-id="element-6200" />
                            </button>
                            <button onClick={() => rejectReview(r.id)} className="p-1 text-rose-600 hover:bg-rose-50 rounded" title="Отклонить" data-id="element-6201">
                              <XCircle className="w-3.5 h-3.5" data-id="element-6202" />
                            </button>
                          </>}
                        {r.status === 'approved' && <button onClick={() => hideReview(r.id)} className="p-1 text-slate-400 hover:bg-slate-100 rounded" title="Скрыть" data-id="element-6203">
                            <XCircle className="w-3.5 h-3.5" data-id="element-6204" />
                          </button>}
                        {r.status === 'hidden' && <button onClick={() => approveReview(r.id)} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded" title="Восстановить" data-id="element-6205">
                            <CheckCircle2 className="w-3.5 h-3.5" data-id="element-6206" />
                          </button>}
                        {r.status === 'rejected' && <button onClick={() => approveReview(r.id)} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded" title="Восстановить" data-id="element-6207">
                            <CheckCircle2 className="w-3.5 h-3.5" data-id="element-6208" />
                          </button>}
                      </div>
                    </td>
                  </tr>)}
                {filteredReviews.length === 0 && <tr data-id="element-6209">
                    <td colSpan={7} className="px-4 py-12 text-center text-sm text-slate-400" data-id="element-6210">
                      Отзывы не найдены
                    </td>
                  </tr>}
              </tbody>
            </table>}

          {/* VIOLATIONS */}
          {activeTab === 'violations' && <table className="w-full text-left" data-id="element-6211">
              <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[10px] uppercase tracking-wider sticky top-0 z-10" data-id="element-6212">
                <tr data-id="element-6213">
                  <th className="px-3 py-2.5 font-medium" data-id="element-6214">ID</th>
                  <th className="px-3 py-2.5 font-medium" data-id="element-6215">Дата</th>
                  <th className="px-3 py-2.5 font-medium" data-id="element-6216">Нарушитель</th>
                  <th className="px-3 py-2.5 font-medium" data-id="element-6217">Действие</th>
                  <th className="px-3 py-2.5 font-medium" data-id="element-6218">Причина</th>
                  <th className="px-3 py-2.5 font-medium" data-id="element-6219">Наказание</th>
                  <th className="px-3 py-2.5 font-medium" data-id="element-6220">Модератор</th>
                  <th className="px-3 py-2.5 font-medium" data-id="element-6221">Статус</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50" data-id="element-6222">
                {violations.map(v => <tr key={v.id} className={`transition-colors ${!v.isActive ? 'opacity-50' : 'hover:bg-slate-50'}`} data-id="element-6223">
                    <td className="px-3 py-2.5 text-xs font-bold text-slate-900" data-id="element-6224">
                      {v.id}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-500" data-id="element-6225">
                      {v.date}
                    </td>
                    <td className="px-3 py-2.5" data-id="element-6226">
                      <div className="flex items-center gap-1.5" data-id="element-6227">
                        <span className={`text-[9px] px-1 py-0.5 rounded ${v.violatorType === 'astrologer' ? 'bg-purple-50 text-purple-600' : 'bg-blue-50 text-blue-600'}`} data-id="element-6228">
                          {v.violatorType === 'astrologer' ? 'Астролог' : 'Юзер'}
                        </span>
                        <span className="text-xs font-medium text-slate-700" data-id="element-6229">
                          {v.violator}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-700" data-id="element-6230">
                      {v.action}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-500 max-w-[200px] truncate" data-id="element-6231">
                      {v.reason}
                    </td>
                    <td className="px-3 py-2.5" data-id="element-6232">
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${v.punishment.toLowerCase().includes('блокировка') ? 'bg-rose-50 text-rose-600' : v.punishment.includes('Предупреждение') ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-600'}`} data-id="element-6233">
                        {v.punishment}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-500" data-id="element-6234">
                      {v.admin}
                    </td>
                    <td className="px-3 py-2.5" data-id="element-6235">
                      {v.isActive ? <span className="text-[10px] font-bold text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded" data-id="element-6236">
                          Активно
                        </span> : <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded" data-id="element-6237">
                          Снято
                        </span>}
                    </td>
                  </tr>)}
              </tbody>
            </table>}

          {/* BLOCKS */}
          {activeTab === 'blocks' && <div className="p-6" data-id="element-6238">
              <div className="space-y-3" data-id="element-6239">
                {violations.filter(v => v.punishment.toLowerCase().includes('блокировка')).map(v => <div key={v.id} className={`flex items-center justify-between p-4 rounded-xl border ${v.isActive ? 'bg-rose-50/50 border-rose-200' : 'bg-slate-50 border-slate-200 opacity-60'}`} data-id="element-6240">
                      <div className="flex items-center gap-3" data-id="element-6241">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${v.isActive ? 'bg-rose-100 text-rose-600' : 'bg-slate-200 text-slate-400'}`} data-id="element-6242">
                          <Ban className="w-5 h-5" data-id="element-6243" />
                        </div>
                        <div data-id="element-6244">
                          <div className="text-sm font-bold text-slate-900" data-id="element-6245">
                            {v.violator}
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-6246">
                            {v.reason}
                          </div>
                          <div className="text-[10px] text-slate-400 mt-0.5" data-id="element-6247">
                            {v.date} · {v.admin}
                          </div>
                          {!v.isActive && <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded mt-1 inline-block" data-id="element-6248">
                              Разблокирован
                            </span>}
                        </div>
                      </div>
                      {v.isActive ? <Button variant="outline" size="sm" className="text-xs" onClick={() => unblockViolation(v.id)} data-id="element-6249">
                          Разблокировать
                        </Button> : <span className="text-xs text-slate-400" data-id="element-6250">Снято</span>}
                    </div>)}
                {violations.filter(v => v.punishment.toLowerCase().includes('блокировка')).length === 0 && <div className="text-center py-12 text-sm text-slate-400" data-id="element-6251">
                    Нет блокировок
                  </div>}
              </div>
            </div>}

          {/* DISPUTES */}
          {activeTab === 'disputes' && <div className="p-6" data-id="element-6252">
              <div className="space-y-3" data-id="element-6253">
                {complaintsList.filter(c => c.hasDispute || c.hasRefund).map(c => <div key={c.id} onClick={() => setSelectedId(c.id)} className={`flex items-center justify-between p-4 bg-white border rounded-xl cursor-pointer transition-colors ${isTerminal(c.status) ? 'border-slate-100 opacity-60' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-6254">
                      <div className="flex items-center gap-3" data-id="element-6255">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${c.hasDispute ? 'bg-amber-100 text-amber-600' : 'bg-purple-100 text-purple-600'}`} data-id="element-6256">
                          {c.hasDispute ? <AlertTriangle className="w-5 h-5" data-id="element-6257" /> : <CreditCard className="w-5 h-5" data-id="element-6258" />}
                        </div>
                        <div data-id="element-6259">
                          <div className="text-sm font-bold text-slate-900" data-id="element-6260">
                            {c.id} · {c.author}
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-6261">
                            {c.description}
                          </div>
                          <div className="flex items-center gap-2 mt-1" data-id="element-6262">
                            {c.hasDispute && <span className="text-[9px] font-bold text-amber-600 bg-amber-50 px-1.5 rounded" data-id="element-6263">
                                Спор
                              </span>}
                            {c.hasRefund && <span className="text-[9px] font-bold text-purple-600 bg-purple-50 px-1.5 rounded" data-id="element-6264">
                                Возврат
                              </span>}
                            {getStatusBadge(c.status)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-6265">
                        {!isTerminal(c.status) && <Button variant="outline" size="sm" className="text-[10px] h-7" onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  resolveComplaint(c.id);
                }} data-id="element-6266">
                            Решить
                          </Button>}
                        <ArrowRight className="w-4 h-4 text-slate-400" data-id="element-6267" />
                      </div>
                    </div>)}
                {complaintsList.filter(c => c.hasDispute || c.hasRefund).length === 0 && <div className="text-center py-12 text-sm text-slate-400" data-id="element-6268">
                    Нет споров и возвратов
                  </div>}
              </div>
            </div>}

          {/* RISKS */}
          {activeTab === 'risks' && <div className="p-6 space-y-3" data-id="element-6269">
              {riskFlags.map(rf => <div key={rf.id} className={`p-4 rounded-xl border ${rf.status === 'resolved' || rf.status === 'dismissed' ? 'bg-slate-50 border-slate-200 opacity-60' : rf.severity === 'high' ? 'bg-rose-50/50 border-rose-200' : 'bg-amber-50/50 border-amber-200'}`} data-id="element-6270">
                  <div className="flex items-start justify-between" data-id="element-6271">
                    <div className="flex items-start gap-3" data-id="element-6272">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${rf.severity === 'high' ? 'bg-rose-100 text-rose-600' : 'bg-amber-100 text-amber-600'}`} data-id="element-6273">
                        <ShieldAlert className="w-4 h-4" data-id="element-6274" />
                      </div>
                      <div data-id="element-6275">
                        <div className="flex items-center gap-2 mb-0.5" data-id="element-6276">
                          <span className="text-sm font-bold text-slate-900" data-id="element-6277">
                            {rf.target}
                          </span>
                          {getRiskBadge(rf.severity)}
                          {getStatusBadge(rf.status)}
                        </div>
                        <p className="text-xs text-slate-600" data-id="element-6278">
                          {rf.description}
                        </p>
                        <span className="text-[10px] text-slate-400" data-id="element-6279">
                          {rf.date}
                        </span>
                      </div>
                    </div>
                    {rf.status === 'active' && <div className="flex gap-1" data-id="element-6280">
                        <Button variant="outline" size="sm" className="text-[10px] h-7" onClick={() => investigateRisk(rf.id)} data-id="element-6281">
                          Проверить
                        </Button>
                        <Button variant="outline" size="sm" className="text-[10px] h-7 text-rose-600 border-rose-200" onClick={() => setConfirmAction({
                  label: `Блокировка ${rf.target}`,
                  action: () => blockFromRisk(rf)
                })} data-id="element-6282">
                          Блокировать
                        </Button>
                      </div>}
                    {rf.status === 'investigating' && <div className="flex gap-1" data-id="element-6283">
                        <Button variant="outline" size="sm" className="text-[10px] h-7" onClick={() => resolveRisk(rf.id)} data-id="element-6284">
                          Закрыть
                        </Button>
                        <Button variant="outline" size="sm" className="text-[10px] h-7 text-rose-600 border-rose-200" onClick={() => setConfirmAction({
                  label: `Блокировка ${rf.target}`,
                  action: () => blockFromRisk(rf)
                })} data-id="element-6285">
                          Блокировать
                        </Button>
                      </div>}
                    {(rf.status === 'resolved' || rf.status === 'dismissed') && <span className="text-xs text-slate-400" data-id="element-6286">Закрыт</span>}
                  </div>
                </div>)}
            </div>}
        </div>

        {/* RIGHT DETAIL PANEL */}
        {selectedItem && <div className="w-80 xl:w-96 flex-shrink-0 border-l border-slate-200 bg-white overflow-y-auto" data-id="element-6287">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10" data-id="element-6288">
              <div data-id="element-6289">
                <div className="text-sm font-bold text-slate-900" data-id="element-6290">
                  {selectedItem.id}
                </div>
                <div className="text-[10px] text-slate-500" data-id="element-6291">
                  {selectedItem.date} · {selectedItem.assignee || 'Не назначен'}
                </div>
              </div>
              <button onClick={() => setSelectedId(null)} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg" data-id="element-6292">
                <X className="w-4 h-4" data-id="element-6293" />
              </button>
            </div>

            <div className="p-4 space-y-5" data-id="element-6294">
              {/* Status badges */}
              <div className="flex items-center gap-2 flex-wrap" data-id="element-6295">
                {getStatusBadge(selectedItem.status)}
                {getPriorityBadge(selectedItem.priority)}
                {getRiskBadge(selectedItem.risk)}
                {selectedItem.isRepeat && <span className="text-[9px] font-bold text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded" data-id="element-6296">
                    Повторная
                  </span>}
                {selectedItem.hasRefund && <span className="text-[9px] font-bold text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded" data-id="element-6297">
                    Возврат
                  </span>}
                {selectedItem.hasDispute && <span className="text-[9px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded" data-id="element-6298">
                    Спор
                  </span>}
              </div>

              {/* Description */}
              <div data-id="element-6299">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-6300">
                  Описание
                </h4>
                <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-lg border border-slate-100" data-id="element-6301">
                  {selectedItem.description}
                </p>
              </div>

              {/* Type & Linked */}
              <div className="grid grid-cols-2 gap-3" data-id="element-6302">
                <div className="p-3 bg-slate-50 rounded-lg" data-id="element-6303">
                  <div className="text-[10px] text-slate-500 mb-1" data-id="element-6304">Тип</div>
                  <div className="flex items-center gap-1" data-id="element-6305">
                    {getTypeIcon(selectedItem.type)}
                    <span className="text-xs font-medium" data-id="element-6306">
                      {getTypeLabel(selectedItem.type)}
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg" data-id="element-6307">
                  <div className="text-[10px] text-slate-500 mb-1" data-id="element-6308">Объект</div>
                  <span className="text-xs font-medium text-indigo-600" data-id="element-6309">
                    {selectedItem.linkedObject}
                  </span>
                </div>
              </div>

              {/* SLA */}
              <div className={`flex items-center justify-between p-3 rounded-lg border ${getSlaColor(selectedItem.slaMinutes)}`} data-id="element-6310">
                <div className="flex items-center gap-2" data-id="element-6311">
                  <Clock className="w-4 h-4" data-id="element-6312" />
                  <span className="text-xs" data-id="element-6313">SLA</span>
                </div>
                <span className="text-xs font-bold" data-id="element-6314">
                  {formatSla(selectedItem.slaMinutes)}
                </span>
              </div>

              {/* Auto Flags */}
              {selectedItem.autoFlags.length > 0 && <div data-id="element-6315">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-6316">
                    Авто-флаги
                  </h4>
                  <div className="space-y-1.5" data-id="element-6317">
                    {selectedItem.autoFlags.map((flag, i) => <div key={i} className="flex items-center gap-2 p-2 bg-rose-50 border border-rose-200 rounded-lg" data-id="element-6318">
                        <AlertOctagon className="w-3.5 h-3.5 text-rose-500 flex-shrink-0" data-id="element-6319" />
                        <span className="text-xs text-rose-700" data-id="element-6320">{flag}</span>
                      </div>)}
                  </div>
                </div>}

              {/* Author */}
              <div data-id="element-6321">
                <button onClick={() => toggleSection('author')} className="flex items-center justify-between w-full mb-2" data-id="element-6322">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-6323">
                    Автор жалобы
                  </h4>
                  {collapsedSections.has('author') ? <ChevronRight className="w-3.5 h-3.5 text-slate-400" data-id="element-6324" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" data-id="element-6325" />}
                </button>
                {!collapsedSections.has('author') && <div className="bg-slate-50 p-3 rounded-lg space-y-2" data-id="element-6326">
                    <div className="flex items-center gap-2" data-id="element-6327">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-[10px] font-bold" data-id="element-6328">
                        {selectedItem.author.substring(0, 2).toUpperCase()}
                      </div>
                      <div data-id="element-6329">
                        <div className="text-xs font-bold text-slate-900" data-id="element-6330">
                          {selectedItem.author}
                        </div>
                        <div className="text-[10px] text-slate-500" data-id="element-6331">
                          {selectedItem.userProfile.email}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs" data-id="element-6332">
                      <div data-id="element-6333">
                        <span className="text-slate-500" data-id="element-6334">LTV:</span>{' '}
                        <span className="font-medium" data-id="element-6335">
                          {selectedItem.userProfile.ltv}
                        </span>
                      </div>
                      <div data-id="element-6336">
                        <span className="text-slate-500" data-id="element-6337">Заказов:</span>{' '}
                        <span className="font-medium" data-id="element-6338">
                          {selectedItem.userProfile.totalOrders}
                        </span>
                      </div>
                      <div data-id="element-6339">
                        <span className="text-slate-500" data-id="element-6340">Рег:</span>{' '}
                        <span className="font-medium" data-id="element-6341">
                          {selectedItem.userProfile.regDate}
                        </span>
                      </div>
                      {selectedItem.userProfile.subscription && <div data-id="element-6342">
                          <span className="text-slate-500" data-id="element-6343">Подписка:</span>{' '}
                          <span className="font-medium text-indigo-600" data-id="element-6344">
                            {selectedItem.userProfile.subscription}
                          </span>
                        </div>}
                    </div>
                  </div>}
              </div>

              {/* Target */}
              <div data-id="element-6345">
                <button onClick={() => toggleSection('target')} className="flex items-center justify-between w-full mb-2" data-id="element-6346">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-6347">
                    Объект жалобы
                  </h4>
                  {collapsedSections.has('target') ? <ChevronRight className="w-3.5 h-3.5 text-slate-400" data-id="element-6348" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" data-id="element-6349" />}
                </button>
                {!collapsedSections.has('target') && <div className="bg-slate-50 p-3 rounded-lg" data-id="element-6350">
                    <div className="flex items-center gap-2 mb-2" data-id="element-6351">
                      <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 text-[10px] font-bold" data-id="element-6352">
                        {selectedItem.target.substring(0, 2).toUpperCase()}
                      </div>
                      <div data-id="element-6353">
                        <div className="text-xs font-bold text-slate-900" data-id="element-6354">
                          {selectedItem.target}
                        </div>
                        <span className="text-[9px] px-1 py-0.5 rounded bg-purple-50 text-purple-600" data-id="element-6355">
                          Астролог
                        </span>
                      </div>
                    </div>
                    {selectedItem.previousComplaints > 0 && <div className="text-[10px] text-rose-600 bg-rose-50 p-2 rounded mb-2" data-id="element-6356">
                        {selectedItem.previousComplaints} предыдущих жалоб на
                        этого астролога
                      </div>}
                  </div>}
              </div>

              {/* Notes */}
              <div data-id="element-6357">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-6358">
                  Заметки ({selectedItem.notes.length})
                </h4>
                {selectedItem.notes.length > 0 && <div className="space-y-2 mb-2 max-h-40 overflow-y-auto" data-id="element-6359">
                    {selectedItem.notes.map((n, i) => <div key={i} className="p-2 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-6360">
                        <p className="text-xs text-slate-700" data-id="element-6361">{n.text}</p>
                        <div className="text-[10px] text-slate-400 mt-1" data-id="element-6362">
                          {n.author} · {n.date}
                        </div>
                      </div>)}
                  </div>}
                <div className="flex gap-2" data-id="element-6363">
                  <input type="text" placeholder="Добавить заметку..." value={noteText} onChange={e => setNoteText(e.target.value)} className="flex-1 text-xs border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-indigo-500" onKeyDown={e => {
                if (e.key === 'Enter' && noteText.trim()) {
                  addNote(selectedItem.id, noteText.trim());
                  setNoteText('');
                }
              }} data-id="element-6364" />
                  <Button variant="outline" size="sm" className="px-2" onClick={() => {
                if (noteText.trim()) {
                  addNote(selectedItem.id, noteText.trim());
                  setNoteText('');
                }
              }} data-id="element-6365">
                    <Plus className="w-3.5 h-3.5" data-id="element-6366" />
                  </Button>
                </div>
              </div>

              {/* Actions */}
              <div data-id="element-6367">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-6368">
                  Действия
                </h4>
                {isTerminal(selectedItem.status) ? <div className="text-center py-4 text-xs text-slate-400 bg-slate-50 rounded-lg" data-id="element-6369">
                    Жалоба{' '}
                    {selectedItem.status === 'resolved' ? 'решена' : 'закрыта'}
                  </div> : <div className="grid grid-cols-2 gap-1.5" data-id="element-6370">
                    {selectedItem.status === 'open' && <button onClick={() => takeInProgress(selectedItem.id)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors" data-id="element-6371">
                        <Clock className="w-3.5 h-3.5" data-id="element-6372" /> В работу
                      </button>}
                    <button onClick={() => resolveComplaint(selectedItem.id)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors" data-id="element-6373">
                      <CheckCircle2 className="w-3.5 h-3.5" data-id="element-6374" /> Решить
                    </button>
                    <button onClick={() => closeComplaint(selectedItem.id)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors" data-id="element-6375">
                      <XCircle className="w-3.5 h-3.5" data-id="element-6376" /> Закрыть
                    </button>
                    <button onClick={() => requestClarification(selectedItem.id)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors" data-id="element-6377">
                      <MessageSquare className="w-3.5 h-3.5" data-id="element-6378" /> Уточнить
                    </button>
                    <button onClick={() => escalateComplaint(selectedItem.id)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors" data-id="element-6379">
                      <ArrowRight className="w-3.5 h-3.5" data-id="element-6380" /> Эскалация
                    </button>
                    <button onClick={() => issueRefund(selectedItem.id)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors" data-id="element-6381">
                      <CreditCard className="w-3.5 h-3.5" data-id="element-6382" /> Возврат
                    </button>
                    <button onClick={() => warnTarget(selectedItem)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors" data-id="element-6383">
                      <AlertTriangle className="w-3.5 h-3.5" data-id="element-6384" /> Предупредить
                    </button>
                    <button onClick={() => restrictTarget(selectedItem)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors" data-id="element-6385">
                      <Lock className="w-3.5 h-3.5" data-id="element-6386" /> Ограничить
                    </button>
                    <button onClick={() => setConfirmAction({
                label: `Блокировка ${selectedItem.target}`,
                action: () => blockTarget(selectedItem)
              })} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-rose-700 bg-rose-50 hover:bg-rose-100 rounded-lg transition-colors col-span-2" data-id="element-6387">
                      <Ban className="w-3.5 h-3.5" data-id="element-6388" /> Заблокировать{' '}
                      {selectedItem.target}
                    </button>
                  </div>}
              </div>

              {/* Action Log for this complaint */}
              {actionLog.filter(a => a.target.includes(selectedItem.id) || a.target.includes(selectedItem.target)).length > 0 && <div data-id="element-6389">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-6390">
                    История действий
                  </h4>
                  <div className="space-y-1.5 max-h-40 overflow-y-auto" data-id="element-6391">
                    {actionLog.filter(a => a.target.includes(selectedItem.id) || a.target.includes(selectedItem.target)).map(a => <div key={a.id} className="p-2 bg-slate-50 rounded-lg" data-id="element-6392">
                          <div className="flex items-center justify-between" data-id="element-6393">
                            <span className="text-xs font-medium text-slate-700" data-id="element-6394">
                              {a.action}
                            </span>
                            <span className="text-[10px] text-slate-400" data-id="element-6395">
                              {a.date}
                            </span>
                          </div>
                          <p className="text-[10px] text-slate-500" data-id="element-6396">
                            {a.details}
                          </p>
                        </div>)}
                  </div>
                </div>}
            </div>
          </div>}
      </div>
    </div>;
}